// A dashboard's section from its action's answer (DashboardPage, BL-189): Marketing's and Operations'
// pages. Loading until the answer comes (skeleton rows, then slow, then Couldn't load), failed with why
// and Try again, and what the page draws once the answer parses as its props: an answer that drifted from
// them says so in one muted sentence rather than half-drawing.
import type { DashboardSection } from '@flowology/ui-web/patterns';

export interface Answer {
  data: unknown;
  error: string | null;
  reload: () => unknown;
}

/** What a section is when its answer is in: its part, or empty with its sentence; its count beside the name. */
export type Drawn = Pick<DashboardSection, 'children' | 'status' | 'emptyText' | 'count'>;

export function answered<T>(
  q: Answer,
  base: { title: string; id?: string; what: string },
  parse: (data: never) => { success: true; data: T } | { success: false },
  draw: (p: T) => Drawn,
): DashboardSection {
  const retry = () => void q.reload();
  if (q.data == null)
    return q.error
      ? { ...base, status: 'failed', failedText: q.error, onRetry: retry }
      : { ...base, status: 'loading', onRetry: retry };
  const parsed = parse(q.data as never);
  if (!parsed.success)
    return { ...base, status: 'empty', emptyText: `Flowology can’t show ${base.what} here.` };
  return { ...base, onRetry: retry, ...draw(parsed.data) };
}

/** A table's first rows (five, DESIGN.md §3), then "Show all n" under them until it's pressed. */
export function firstOf<R>(
  rows: readonly R[],
  first: number,
  all: boolean,
  showAll: () => void,
): { rows: R[]; more: { label: string; onPress: () => void } | null } {
  if (all || rows.length <= first) return { rows: [...rows], more: null };
  return { rows: rows.slice(0, first), more: { label: `Show all ${rows.length}`, onPress: showAll } };
}
